import { hydrateGoodsFromQuoteDescription, resolveCompanyState, getCompanyOptionsFromSession } from "./quoteValidation";

export const fixResponseObject = (obj) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key.charAt(0).toLowerCase() + key.slice(1),
      value,
    ]),
  );

export const fixResponseListObject = (obj) =>
  obj.map((element) =>
    Object.fromEntries(
      Object.entries(element).map(([key, value]) => [
        key.charAt(0).toLowerCase() + key.slice(1),
        value,
      ]),
    ),
  );

export const normalizeQuoteGetResponse = (response) => {
  const fixedQuoteResponse = fixResponseObject(response.Quote);
  const fixedGoodsResponse = hydrateGoodsFromQuoteDescription(
    fixResponseListObject(response.Goods || []),
    fixedQuoteResponse,
  );
  const fixedEquipmentResponse = fixResponseListObject(response.Equipment || []);
  const fixedChargesResponse = response.Charges
    ? fixResponseListObject(response.Charges)
    : [];
  const fixedChargesResponseWithId = fixedChargesResponse.map((value, index) => ({
    ...value,
    id: index,
  }));

  delete fixedQuoteResponse.Goods;
  delete fixedQuoteResponse.Equipment;
  delete fixedQuoteResponse.Charges;

  const fixedQuoteResponseDates = {
    ...fixedQuoteResponse,
    startDate: fixedQuoteResponse.startDate?.split("T")[0],
    dueDate: fixedQuoteResponse.dueDate?.split("T")[0],
    expireDate: fixedQuoteResponse.expireDate?.split("T")[0],
  };

  const fixedEquipmentTypesResponse = fixedEquipmentResponse.map((equipment) => ({
    ...equipment,
    equipmentType: `${equipment.equipmentTypeLenght || ""}${equipment.equipmentTypeCode || ""}`,
  }));

  return resolveCompanyState({
    ...fixedQuoteResponseDates,
    Goods: fixedGoodsResponse,
    Equipments: fixedEquipmentTypesResponse,
    charges: fixedChargesResponseWithId,
    companyOptions: getCompanyOptionsFromSession(),
    isRevisionView: true,
  });
};

const SCALAR_COMPARE_FIELDS = [
  "name",
  "quoteCode",
  "reference",
  "remarks",
  "currencyCode",
  "startDate",
  "dueDate",
  "expireDate",
  "originalPortName",
  "loadingPortName",
  "dischargePortName",
  "finalPortName",
  "clientName",
  "clientCode",
  "clientVat",
  "shipperCode",
  "shipperVat",
  "consigneeCode",
  "consigneeVat",
];

const CHARGE_COMPARE_FIELDS = [
  "serviceCode",
  "currencyCode",
  "salesValue",
  "multiple",
  "quantity",
  "costValue",
];

const EQUIPMENT_COMPARE_FIELDS = [
  "quantity",
  "equipmentType",
  "equipmentTypeCode",
  "grossWeight",
  "measures",
];

const GOODS_COMPARE_FIELDS = [
  "commodityCode",
  "commodityName",
  "dangerousGoodsCode",
  "grossWeight",
  "measures",
  "goodsDescription",
];

const formatValue = (value) => {
  if (value == null || value === "") return "";
  return String(value);
};

const compareArrayRows = (section, fields, currentArr = [], previousArr = [], changed) => {
  const max = Math.max(currentArr.length, previousArr.length);
  for (let i = 0; i < max; i += 1) {
    const curr = currentArr[i];
    const prev = previousArr[i];
    fields.forEach((field) => {
      if (formatValue(prev?.[field]) !== formatValue(curr?.[field])) {
        changed.push(`${section}:${i}:${field}`);
      }
    });
  }
};

const FK_HIGHLIGHT_GROUPS = [
  { highlight: "clientName", fields: ["clientName", "clientCode", "clientVat"] },
  { highlight: "shipperCode", fields: ["shipperCode", "shipperVat"] },
  { highlight: "consigneeCode", fields: ["consigneeCode", "consigneeVat"] },
];

export const buildRevisionChangedFields = (current, previous) => {
  if (!previous) return [];

  const changed = [];

  SCALAR_COMPARE_FIELDS.forEach((key) => {
    if (formatValue(previous[key]) !== formatValue(current[key])) {
      changed.push(key);
    }
  });

  FK_HIGHLIGHT_GROUPS.forEach(({ highlight, fields }) => {
    if (
      fields.some(
        (field) => formatValue(previous[field]) !== formatValue(current[field]),
      ) &&
      !changed.includes(highlight)
    ) {
      changed.push(highlight);
    }
  });

  compareArrayRows(
    "charge",
    CHARGE_COMPARE_FIELDS,
    current.charges,
    previous.charges,
    changed,
  );
  compareArrayRows(
    "equipment",
    EQUIPMENT_COMPARE_FIELDS,
    current.Equipments,
    previous.Equipments,
    changed,
  );
  compareArrayRows(
    "goods",
    GOODS_COMPARE_FIELDS,
    current.Goods,
    previous.Goods,
    changed,
  );

  return changed;
};

export const isRevisionFieldChanged = (dataState, section, index, fieldCode) => {
  const changedFields = dataState?.revisionChangedFields;
  if (!changedFields?.length) return false;

  if (!section) {
    return changedFields.includes(fieldCode);
  }

  return changedFields.includes(`${section}:${index}:${fieldCode}`);
};

export const revisionHighlightColors = (dataState, section, index, fieldCode, requiredMissing) => {
  if (isRevisionFieldChanged(dataState, section, index, fieldCode)) {
    return {
      color: "var(--color-yellow)",
      secondColor: "var(--color-yellow)",
      disabledColor: "var(--color-yellow)",
    };
  }

  if (requiredMissing) {
    return {
      color: "var(--color-red)",
      secondColor: "var(--color-red)",
      disabledColor: "var(--color-red)",
    };
  }

  return {};
};

export const revisionCellStyle = (dataState, section, index, fieldCode) =>
  isRevisionFieldChanged(dataState, section, index, fieldCode)
    ? { backgroundColor: "var(--color-yellow)", display: "block", padding: "4px 8px", borderRadius: "4px" }
    : {};
